import { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { adminAuth as auth } from "../utils/adminFirebase";

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // true until Firebase confirms initial state
  const [showLogin, setShowLogin] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  // Gates the "Premiums Config" sidebar section. Intentionally plain
  // component state (no localStorage/sessionStorage) so it is always
  // false again on a fresh page load or refresh, even if the person is
  // still signed in to Firebase from before - it only flips to true
  // once they explicitly unlock it during the current page session.
  const [premiumUnlocked, setPremiumUnlocked] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  function unlockPremium() {
    setPremiumUnlocked(true);
  }

  async function login(email, password) {
    setChecking(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setShowLogin(false);
      setPremiumUnlocked(true);
      return true;
    } catch (err) {
      console.error("[AdminAuth] login error:", err.code);
      if (err.code === "auth/invalid-credential" || err.code === "auth/wrong-password" || err.code === "auth/user-not-found") {
        setError("Incorrect email or password.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Try again later.");
      } else {
        setError("Login failed. Try again.");
      }
      return false;
    } finally {
      setChecking(false);
    }
  }

  async function logout() {
    await signOut(auth);
    setPremiumUnlocked(false);
  }

  return (
    <AdminAuthContext.Provider
      value={{
        isAuthed: !!user,
        user,
        authLoading,
        login,
        logout,
        showLogin,
        setShowLogin,
        error,
        setError,
        checking,
        premiumUnlocked,
        unlockPremium,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}